// This file is more or less a copy of usePaginatedCollection in the gonfalon hooks folder as it's needed for the @gonfalon/pagination package but not exported by gonfalon.
import { useCallback, useMemo, useState } from 'react';

type UseClientPaginationOptions<T> = {
  items: T[];
  pageSize: number;
  resetKey?: string;
};

type UseClientPaginationResult<T> = {
  pageItems: T[];
  offset: number;
  totalCount: number;
  from: number;
  to: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  gotoFirstPage: () => void;
  gotoPreviousPage: () => void;
  gotoNextPage: () => void;
  gotoLastPage: () => void;
};

export const useClientPagination = <T>({
  items,
  pageSize,
  resetKey,
}: UseClientPaginationOptions<T>): UseClientPaginationResult<T> => {
  const [offset, setOffset] = useState(0);
  const [prevResetKey, setPrevResetKey] = useState(resetKey);
  if (resetKey !== prevResetKey) {
    setPrevResetKey(resetKey);
    setOffset(0);
  }

  const totalCount = items.length;
  const maxOffset = totalCount > pageSize ? Math.floor((totalCount - 1) / pageSize) * pageSize : 0;
  if (offset > maxOffset) {
    setOffset(0);
  }

  const pageItems = useMemo(() => items.slice(offset, offset + pageSize), [items, offset, pageSize]);

  const hasPreviousPage = offset > 0;
  const hasNextPage = offset + pageSize < totalCount;

  const gotoFirstPage = useCallback(() => setOffset(0), []);
  const gotoPreviousPage = useCallback(() => setOffset((prevOffset) => Math.max(0, prevOffset - pageSize)), [pageSize]);
  const gotoNextPage = useCallback(
    () => setOffset((prevOffset) => Math.min(maxOffset, prevOffset + pageSize)),
    [pageSize, maxOffset],
  );
  const gotoLastPage = useCallback(() => setOffset(maxOffset), [maxOffset]);

  const from = totalCount === 0 ? 0 : offset + 1;
  const to = Math.min(offset + pageSize, totalCount);

  return {
    pageItems,
    offset,
    totalCount,
    from,
    to,
    hasPreviousPage,
    hasNextPage,
    gotoFirstPage,
    gotoPreviousPage,
    gotoNextPage,
    gotoLastPage,
  };
};
